import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Edit3, ArrowDown } from "lucide-react";

const ROUND_ORDER = ["R256", "R128", "R64", "R32", "R16", "QF", "SF", "QU", "F"];
const ROUND_LABELS = {
  R256: "Turno Prel.", R128: "128-esimi", R64: "64-esimi", R32: "32-esimi",
  R16: "Sedicesimi", QF: "Quarti", SF: "Semifinale", QU: "Qualificazione", F: "Finale",
};
const SECTION_ORDER = ["4.NC", "4.x", "3.x", "2.x", "1.x"];
const isByeMatch = (m) => m.status === "bye" || m.player1_id === "BYE" || m.player2_id === "BYE";

/* ─── Compute standard round name for QU rounds based on match count ─── */
// DEPRECATED per-section helpers — replaced by global depth computation below
const DEPTH_NAMES = ["Finale", "Semifinale", "Quarti", "Ottavi", "Sedicesimi", "32-esimi", "64-esimi", "128-esimi", "256-esimi"];
const DEPTH_CODES = ["F", "SF", "QF", "R8", "R16", "R32", "R64", "R128", "R256"];

function computeGlobalRoundInfo(matches) {
  const matchMap = {};
  for (const m of matches) matchMap[m.match_id] = m;

  // Build reverse map: match_id -> list of feeder match_ids
  const feeders = {};
  for (const m of matches) {
    if (m.next_match_id) {
      if (!feeders[m.next_match_id]) feeders[m.next_match_id] = [];
      feeders[m.next_match_id].push(m.match_id);
    }
  }

  // Find the final(s) — matches with no next_match_id and round === "F"
  let finals = matches.filter(m => !m.next_match_id && m.round === "F");
  if (finals.length === 0) finals = matches.filter(m => !m.next_match_id);

  // BFS from finals to assign depth (distance from final)
  const depth = {};
  const queue = [];
  for (const f of finals) { depth[f.match_id] = 0; queue.push({ id: f.match_id, d: 0 }); }
  while (queue.length > 0) {
    const { id, d } = queue.shift();
    for (const fid of (feeders[id] || [])) {
      if (depth[fid] === undefined) {
        depth[fid] = d + 1;
        queue.push({ id: fid, d: d + 1 });
      }
    }
  }

  // Build round display name per (section, round_index)
  const sriDepth = {}; // key "section|ri" -> depth
  for (const m of matches) {
    const key = `${m.section}|${m.round_index ?? 0}`;
    const d = depth[m.match_id];
    if (d !== undefined) {
      if (sriDepth[key] === undefined || d === sriDepth[key]) sriDepth[key] = d;
      else sriDepth[key] = Math.max(sriDepth[key], d); // use max depth for the group
    }
  }

  const roundNameMap = {}; // "section|ri" -> display name
  for (const [key, d] of Object.entries(sriDepth)) {
    roundNameMap[key] = DEPTH_NAMES[d] || `${Math.pow(2, d)}-esimi`;
  }

  // Build label overrides for ALL matches based on global depth
  const labelOverrides = {};
  for (const m of matches) {
    const d = depth[m.match_id];
    if (d !== undefined) {
      const code = DEPTH_CODES[d] || `R${Math.pow(2, d)}`;
      if (code === "F") labelOverrides[m.match_id] = "F";
      else if (code === "SF") labelOverrides[m.match_id] = `SF${(m.position_in_round ?? 0) + 1}`;
      else if (code === "QF") labelOverrides[m.match_id] = `QF${(m.position_in_round ?? 0) + 1}`;
      else labelOverrides[m.match_id] = `${code}-${(m.position_in_round ?? 0) + 1}`;
    }
  }

  return { roundNameMap, labelOverrides };
}

/* ─── sizing constants ─── */
const MATCH_W = 200;       // match box width px
const MATCH_H = 48;        // match box height px (2 rows × 24px)
const COL_GAP = 32;        // horizontal gap between round columns
const ROUND_HEADER_H = 32; // header height for round labels
const LINE_COLOR = "#1e293b"; // dark slate for connector lines
const LINE_WIDTH = 2;        // connector line thickness

/* ─── Player Row (single player line) ─── */
const PlayerRow = ({ name, playerId, isWinner, isBye, onEditName, matchId, position, seedNum, ranking }) => {
  const displayName = name || (isBye ? "BYE" : "TBD");
  const isByePlayer = playerId === "BYE" || isBye;
  const isQualPlaceholder = playerId && typeof playerId === "string" && playerId.startsWith("QUAL_");
  const canEdit = !!onEditName;
  const showRanking = ranking && playerId && playerId !== "BYE" && !isByePlayer && !isQualPlaceholder && !displayName.trim().endsWith(ranking);
  const len = displayName.length + (seedNum ? 4 : 0) + (showRanking ? ranking.length + 1 : 0);
  const fontSize = len > 32 ? "text-[7px]" : len > 26 ? "text-[8px]" : len > 20 ? "text-[9px]" : "text-[10px]";

  return (
    <div
      className={`flex items-center h-[24px] px-1.5 group ${
        isWinner ? "font-bold text-green-900 bg-green-50" :
        isByePlayer ? "text-slate-400 italic" :
        isQualPlaceholder ? "text-amber-700 italic bg-amber-50/40" :
        !playerId ? "text-slate-400 italic" : ""
      }`}
    >
      {seedNum && <span className="text-[9px] font-bold text-green-700 mr-0.5 shrink-0">({seedNum})</span>}
      <span className={`flex-1 whitespace-nowrap overflow-hidden ${fontSize} leading-tight`} style={{textOverflow: 'clip'}}>
        {displayName}
      </span>
      {showRanking && <span className="text-[8px] font-semibold text-blue-600 ml-0.5 shrink-0">{ranking}</span>}
      {canEdit && (
        <button
          data-testid={`edit-name-${matchId}-${position}`}
          onClick={(e) => { e.stopPropagation(); onEditName({ playerId, currentName: name, matchId, position }); }}
          className="ml-0.5 p-0.5 rounded-sm hover:bg-slate-200 transition-colors shrink-0"
        >
          <Edit3 className="h-2.5 w-2.5 text-slate-500" />
        </button>
      )}
    </div>
  );
};

/* ─── Match Box ─── */
const MatchBox = ({ match, x, y, onClick, onEditName, seedMap = {} }) => {
  const isCompleted = match.status === "completed";
  const isBye = match.status === "bye";
  const p1Seed = match.player1_seed || (match.player1_id ? seedMap[match.player1_id] : null);
  const p2Seed = match.player2_seed || (match.player2_id ? seedMap[match.player2_id] : null);

  return (
    <div
      data-testid={`match-card-${match.match_id}`}
      onClick={() => onClick?.(match)}
      className={`absolute cursor-pointer transition-shadow duration-150 hover:shadow-md overflow-hidden ${
        isCompleted ? "bg-white" :
        isBye ? "bg-slate-200/80" :
        "bg-white hover:border-green-600"
      }`}
      style={{
        left: x, top: y, width: MATCH_W, height: MATCH_H,
        border: `1.5px solid ${isCompleted ? '#16a34a' : isBye ? '#94a3b8' : '#334155'}`,
        zIndex: isBye ? 0 : 2,
      }}
    >
      <PlayerRow
        name={match.player1_name} playerId={match.player1_id}
        isWinner={match.winner_id === match.player1_id}
        isBye={match.player1_id === "BYE"} onEditName={onEditName}
        matchId={match.match_id} position="player1"
        seedNum={p1Seed} ranking={match.player1_ranking}
      />
      <div style={{ borderTop: '1px solid #334155' }} />
      <PlayerRow
        name={match.player2_name} playerId={match.player2_id}
        isWinner={match.winner_id === match.player2_id}
        isBye={match.player2_id === "BYE" || (isBye && !match.player2_id)}
        onEditName={onEditName}
        matchId={match.match_id} position="player2"
        seedNum={p2Seed} ranking={match.player2_ranking}
      />
    </div>
  );
};

/* ─── Match label floating ─── */
const MatchLabel = ({ label, section, x, y }) => (
  <div
    className="absolute text-[8px] font-bold text-slate-400 uppercase tracking-wider text-center pointer-events-none"
    style={{ left: x, top: y - 11, width: MATCH_W }}
  >
    {section && <span className="text-amber-600">[{section}] </span>}
    {label}
  </div>
);

/* ─── SVG Connector Lines ─── */
const ConnectorLines = ({ connections, svgW, svgH }) => (
  <svg
    className="absolute inset-0 pointer-events-none"
    width={svgW} height={svgH}
    style={{ zIndex: 1, pointerEvents: 'none' }}
  >
    {connections.map((c, i) => {
      if (c.type === "line") {
        return (
          <line key={i} x1={c.x1} y1={c.y1} x2={c.x2} y2={c.y2}
            stroke={LINE_COLOR} strokeWidth={LINE_WIDTH} />
        );
      }
      // Default: bracket style
      return (
        <g key={i}>
          <line x1={c.x1} y1={c.y1} x2={c.mx} y2={c.y1} stroke={LINE_COLOR} strokeWidth={LINE_WIDTH} />
          <line x1={c.x1} y1={c.y2} x2={c.mx} y2={c.y2} stroke={LINE_COLOR} strokeWidth={LINE_WIDTH} />
          <line x1={c.mx} y1={c.y1} x2={c.mx} y2={c.y2} stroke={LINE_COLOR} strokeWidth={LINE_WIDTH} />
          <line x1={c.mx} y1={c.my} x2={c.x2} y2={c.my} stroke={LINE_COLOR} strokeWidth={LINE_WIDTH} />
        </g>
      );
    })}
  </svg>
);


/* ─── Layout engine: compute (x, y) for every match ─── */
function layoutBracket(roundsArr, matchByIdMap) {
  // roundsArr: [{roundName, matches:[m1,m2,...]}] left→right
  // returns {positions: Map<matchId, {x,y}>, connections:[], width, height}

  if (roundsArr.length === 0) return { positions: new Map(), connections: [], width: 0, height: 0 };

  const positions = new Map();
  const connections = [];

  // First pass: lay out first round evenly
  const firstRound = roundsArr[0];
  const firstMatches = firstRound.matches;
  // Scale vertical gap based on number of rounds — more rounds = more spacing needed in R1
  const numRounds = roundsArr.length;
  const baseGap = numRounds <= 2 ? 24 : numRounds <= 3 ? 28 : 32;
  const vGap = baseGap;
  const totalFirstH = firstMatches.length * MATCH_H + (firstMatches.length - 1) * vGap;

  const x0 = 0;
  firstMatches.forEach((m, idx) => {
    const y = ROUND_HEADER_H + idx * (MATCH_H + vGap);
    positions.set(m.match_id, { x: x0, y });
  });

  // Subsequent rounds: position each match vertically centered between its two feeder matches
  for (let rIdx = 1; rIdx < roundsArr.length; rIdx++) {
    const colX = rIdx * (MATCH_W + COL_GAP);
    const roundMatches = roundsArr[rIdx].matches;

    roundMatches.forEach((m, mIdx) => {
      // Find the two feeder matches that link to this match
      const feeders = [];
      const prevRound = roundsArr[rIdx - 1].matches;
      for (const pm of prevRound) {
        if (pm.next_match_id === m.match_id) {
          feeders.push(pm);
        }
      }
      // Also check ALL previous rounds (not just immediate prev) for cross-round links
      if (feeders.length < 2) {
        for (let pIdx = 0; pIdx < rIdx; pIdx++) {
          for (const pm of roundsArr[pIdx].matches) {
            if (pm.next_match_id === m.match_id && !feeders.find(f => f.match_id === pm.match_id)) {
              feeders.push(pm);
            }
          }
        }
      }

      let y;
      if (feeders.length >= 2) {
        // Center between two feeders
        const pos0 = positions.get(feeders[0].match_id);
        const pos1 = positions.get(feeders[1].match_id);
        if (pos0 && pos1) {
          const minY = Math.min(pos0.y, pos1.y);
          const maxY = Math.max(pos0.y, pos1.y);
          y = minY + (maxY - minY + MATCH_H) / 2 - MATCH_H / 2;
        } else {
          y = ROUND_HEADER_H + mIdx * (MATCH_H + vGap * Math.pow(2, rIdx));
        }
      } else if (feeders.length === 1) {
        const pos0 = positions.get(feeders[0].match_id);
        y = pos0 ? pos0.y : ROUND_HEADER_H + mIdx * (MATCH_H + vGap * Math.pow(2, rIdx));
      } else {
        // No feeders found, use default spacing
        y = ROUND_HEADER_H + mIdx * (MATCH_H + vGap * Math.pow(2, rIdx));
      }
      positions.set(m.match_id, { x: colX, y });

      // Build connector lines — ALWAYS draw brackets for ALL matches
      if (feeders.length >= 2) {
        const pos0 = positions.get(feeders[0].match_id);
        const pos1 = positions.get(feeders[1].match_id);
        if (pos0 && pos1) {
          const topFeeder = pos0.y < pos1.y ? pos0 : pos1;
          const botFeeder = pos0.y < pos1.y ? pos1 : pos0;
          const mx = topFeeder.x + MATCH_W + (colX - topFeeder.x - MATCH_W) / 2;
          connections.push({
            type: "bracket",
            x1: topFeeder.x + MATCH_W,
            y1: topFeeder.y + MATCH_H / 2,
            y2: botFeeder.y + MATCH_H / 2,
            mx: mx,
            my: y + MATCH_H / 2,
            x2: colX,
          });
        }
      } else if (feeders.length === 1) {
        const fPos = positions.get(feeders[0].match_id);
        if (fPos) {
          connections.push({
            type: "line",
            x1: fPos.x + MATCH_W, y1: fPos.y + MATCH_H / 2,
            x2: colX, y2: y + MATCH_H / 2,
          });
        }
      }
    });
  }

  // Calculate total dimensions — add extra width for qualifier labels that extend beyond the last column
  const allPos = [...positions.values()];
  const width = roundsArr.length * (MATCH_W + COL_GAP) + 120;
  const height = Math.max(...allPos.map(p => p.y + MATCH_H)) + 20;

  return { positions, connections, width, height };
}


/* ─── Section Bracket with absolute positioning + SVG lines ─── */
const SectionBracket = ({ sectionName, sectionMatches, onMatchClick, onEditPlayerName, conflictSet, seedMap, globalRoundInfo, matchSectionMap, courtNames = {} }) => {
  // Group by round_index, sort within
  const byRoundIdx = {};
  for (const m of sectionMatches) {
    const ri = m.round_index ?? 0;
    if (!byRoundIdx[ri]) byRoundIdx[ri] = [];
    byRoundIdx[ri].push(m);
  }
  for (const ri of Object.keys(byRoundIdx)) {
    byRoundIdx[ri].sort((a, b) => a.position_in_round - b.position_in_round);
  }

  const sortedRounds = Object.keys(byRoundIdx).sort((a, b) => Number(a) - Number(b));
  const roundsArr = sortedRounds.map(ri => ({
    roundName: byRoundIdx[ri][0]?.round || `R${ri}`,
    matches: byRoundIdx[ri],
  }));

  // Use global round info (distance from Final) for column headers and match labels
  const section = sectionMatches[0]?.section;
  const roundDisplayInfo = roundsArr.map((r, rIdx) => {
    const ri = sortedRounds[rIdx];
    const key = `${section}|${ri}`;
    const globalName = globalRoundInfo?.roundNameMap?.[key];
    if (globalName) return { name: globalName };
    return { name: ROUND_LABELS[r.roundName] || r.roundName };
  });
  const labelOverrides = globalRoundInfo?.labelOverrides || {};

  // Build id map
  const matchByIdMap = {};
  for (const m of sectionMatches) matchByIdMap[m.match_id] = m;

  const { positions, connections, width, height } = layoutBracket(roundsArr, matchByIdMap);

  if (positions.size === 0) return null;

  return (
    <div className="mb-4" style={{ minWidth: width }}>
      {sectionName && (
        <div className="text-sm font-bold uppercase tracking-widest text-center mb-2 py-1.5 bg-amber-50 border border-amber-200 rounded-sm text-amber-800">
          Sezione {sectionName}
        </div>
      )}
      {/* Round headers */}
      <div className="relative" style={{ height: ROUND_HEADER_H, minWidth: width }}>
        {roundsArr.map((r, rIdx) => (
          <div
            key={r.roundName + rIdx}
            className="absolute text-[10px] font-bold uppercase tracking-widest text-center text-slate-700 border-b border-slate-800 pb-1"
            style={{ left: rIdx * (MATCH_W + COL_GAP), width: MATCH_W, top: 8 }}
          >
            {roundDisplayInfo[rIdx].name}
          </div>
        ))}
      </div>
      {/* Bracket area */}
      <div className="relative" style={{ width: width, minWidth: width, height: height - ROUND_HEADER_H + 10 }}>
        <ConnectorLines connections={connections} svgW={width} svgH={height} />
        {sectionMatches.map(m => {
          const pos = positions.get(m.match_id);
          if (!pos) return null;
          const bye = isByeMatch(m);
          const isCompleted = m.status === "completed";
          const hasSchedule = !bye && (m.scheduled_time || m.scheduled_court != null);
          // Format date as DD/MM/YY
          let schedText = '';
          if (hasSchedule) {
            const parts = [];
            if (m.scheduled_court != null) parts.push((() => {
              const num = String(m.scheduled_court);
              if (courtNames && m.scheduled_date && courtNames[m.scheduled_date]) {
                const n = courtNames[m.scheduled_date][num] || courtNames[m.scheduled_date][m.scheduled_court];
                if (n && n.trim()) return n.trim();
              }
              if (courtNames) {
                for (const dayNames of Object.values(courtNames)) {
                  if (typeof dayNames === "object" && dayNames) {
                    const n = dayNames[num] || dayNames[m.scheduled_court];
                    if (n && n.trim()) return n.trim();
                  }
                }
              }
              return `C${m.scheduled_court}`;
            })());
            if (m.scheduled_time) parts.push(m.scheduled_time);
            if (m.scheduled_date) {
              const d = m.scheduled_date.split('-');
              if (d.length === 3) parts.push(`${d[2]}/${d[1]}/${d[0].slice(2)}`);
            }
            schedText = parts.join(' ');
          }
          // After completion, append score and (optional) duration to the line
          // shown below the match — visible at-a-glance directly on the bracket.
          let resultText = '';
          if (isCompleted) {
            const segs = [];
            if (m.score) segs.push(m.score);
            if (m.duration_minutes) segs.push(`${m.duration_minutes}′`);
            resultText = segs.join('  ');
          }
          return (
            <div key={m.match_id}>
              <MatchLabel label={labelOverrides[m.match_id] || m.match_label} section={null} x={pos.x} y={pos.y} />
              <MatchBox
                match={m} x={pos.x} y={pos.y}
                onClick={onMatchClick} onEditName={onEditPlayerName}
                seedMap={seedMap}
              />
              {hasSchedule && (
                <div
                  className="absolute text-[10px] font-medium text-slate-600 text-center whitespace-nowrap pointer-events-none"
                  style={{ left: pos.x, top: pos.y + MATCH_H + 2, width: MATCH_W }}
                >
                  {schedText}
                  {resultText && (
                    <span
                      className="ml-1.5 text-green-700 font-bold"
                      data-testid={`bracket-result-${m.match_id}`}
                    >
                      • {resultText}
                    </span>
                  )}
                </div>
              )}
              {!hasSchedule && resultText && (
                <div
                  className="absolute text-[10px] font-bold text-green-700 text-center whitespace-nowrap pointer-events-none"
                  style={{ left: pos.x, top: pos.y + MATCH_H + 2, width: MATCH_W }}
                  data-testid={`bracket-result-${m.match_id}`}
                >
                  {resultText}
                </div>
              )}
              {/* Cross-section qualifier reference: line + green label with Q{n} format */}
              {m.next_match_id && matchSectionMap && matchSectionMap[m.next_match_id] && matchSectionMap[m.next_match_id] !== m.section && (
                <div
                  className="absolute flex items-center pointer-events-none"
                  style={{ left: pos.x + MATCH_W + 4, top: pos.y + MATCH_H / 2 - 8, whiteSpace: 'nowrap' }}
                >
                  <div style={{ width: 28, height: LINE_WIDTH, background: LINE_COLOR, borderRadius: 1 }} />
                  <span className="text-[11px] font-bold text-green-600 ml-1.5 whitespace-nowrap">
                    Q{(m.position_in_round ?? 0) + 1} Sez.{m.section}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SectionConnector = ({ fromSection, toSection, qualifiers }) => (
  <div className="flex items-center justify-center -my-1" data-testid={`section-connector-${fromSection}-${toSection}`}>
    <div className="flex flex-col items-center gap-0.5">
      <div className="w-px h-3 bg-gradient-to-b from-amber-300 to-green-500" />
      <div className="px-4 py-1.5 border-2 border-dashed border-green-400 rounded-lg bg-green-50/80 shadow-sm">
        <div className="text-[10px] font-semibold text-green-700 tracking-wide uppercase text-center mb-1">
          {qualifiers.length} qualificati verso Sez. {toSection}
        </div>
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-0.5">
          {qualifiers.map((q, i) => (
            <span key={i} className="text-[9px] text-green-800 font-medium whitespace-nowrap">
              {q.label} Sez. {fromSection} → {q.destLabel}
            </span>
          ))}
        </div>
      </div>
      <div className="w-px h-3 bg-gradient-to-b from-green-500 to-amber-300" />
    </div>
  </div>
);


/* ─── Main Exported Component ─── */
export const TournamentBracket = ({ matches = [], rounds = {}, onMatchClick, onEditPlayerName, conflictSet, courtNames = {} }) => {
  const seedMap = {};
  for (const m of matches) {
    if (m.player1_seed && m.player1_id) seedMap[m.player1_id] = m.player1_seed;
    if (m.player2_seed && m.player2_id) seedMap[m.player2_id] = m.player2_seed;
  }

  if (matches.length === 0) {
    return <div className="text-center py-12 text-slate-500" data-testid="bracket-empty">Nessun tabellone generato</div>;
  }

  const globalRoundInfo = computeGlobalRoundInfo(matches);

  const sections = [...new Set(matches.map(m => m.section).filter(Boolean))];
  const hasMultipleSections = sections.length > 1;

  if (!hasMultipleSections) {
    // Single section: render all matches as one bracket
    return (
      <ScrollArea className="w-full" data-testid="tournament-bracket">
        <div className="p-4">
          <SectionBracket
            sectionName={null}
            sectionMatches={matches}
            onMatchClick={onMatchClick}
            onEditPlayerName={onEditPlayerName}
            conflictSet={conflictSet}
            seedMap={seedMap}
            globalRoundInfo={globalRoundInfo}
            courtNames={courtNames}
          />
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    );
  }

  // Multi-section
  const sectionOrder = [...sections].sort((a, b) => {
    const aIdx = SECTION_ORDER.indexOf(a);
    const bIdx = SECTION_ORDER.indexOf(b);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return a.localeCompare(b);
  });

  // Build a lookup: matchId -> section, to count only cross-section qualifiers
  const matchSectionMap = {};
  for (const m of matches) matchSectionMap[m.match_id] = m.section;

  return (
    <ScrollArea className="w-full" data-testid="tournament-bracket">
      <div className="min-w-max p-4 space-y-6">
        {sectionOrder.map((section, idx) => {
          const sectionMatches = matches.filter(m => m.section === section);
          const nextSection = sectionOrder[idx + 1];
          // Collect individual qualifier details for the connector
          const qualifiers = [];
          if (nextSection) {
            for (const m of sectionMatches) {
              if (m.round === "QU" && m.next_match_id && matchSectionMap[m.next_match_id] === nextSection) {
                const destMatch = matches.find(dm => dm.match_id === m.next_match_id);
                qualifiers.push({
                  label: m.match_label,
                  destLabel: destMatch ? destMatch.match_label : '',
                });
              }
            }
          }
          return (
            <div key={section}>
              <SectionBracket
                sectionName={section}
                sectionMatches={sectionMatches}
                onMatchClick={onMatchClick}
                onEditPlayerName={onEditPlayerName}
                conflictSet={conflictSet}
                seedMap={seedMap}
                globalRoundInfo={globalRoundInfo}
                matchSectionMap={matchSectionMap}
                courtNames={courtNames}
              />
              {nextSection && qualifiers.length > 0 && (
                <SectionConnector
                  fromSection={section}
                  toSection={nextSection}
                  qualifiers={qualifiers}
                />
              )}
            </div>
          );
        })}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
};
